import { track } from "@/lib/analytics"
import { create } from "zustand"
import { noteRunStart, trackDraftCompleted } from "../order-sim/simAnalytics"
import { followThread } from "./stream"
import { getDraft, getThread } from "./api"
import { applyRunThrough, emptyRunThrough, runThroughSnapshot, type RunThroughFrame, type RunThroughState } from "../order-sim/runThrough"
import type {
  AnswerPayload, CheckpointPayload, StageLandedPayload, DocumentRequestedPayload, DraftSnapshot, SectionDraft, QuestionPayload, RunErrorPayload, RunFinishedPayload, RunStartedPayload, StepKind, StepPayload, StepStatus, EventFrame, PendingQuestion, Stage, ThreadSnapshot, CaseFacts,
} from "./contract"

export interface StepView {
  /** Step id; old payloads without one get `seq-<thread_seq>`. */
  id: string
  threadSeq: number
  stage: Stage | null
  /** The label (kept as `message` for older readers). */
  message: string
  status: StepStatus
  kind: StepKind
  detail: string | null
}
export interface CheckpointView extends CheckpointPayload { threadSeq: number }
/** A conversational event kept in order so the chat can show past questions and requests after a refresh. */
export type ChatEvent = { threadSeq: number; runId?: string; createdAt?: string } & (
  | { type: "run.started"; payload: RunStartedPayload }
  | { type: "step"; payload: StepPayload }
  | { type: "question"; payload: QuestionPayload }
  | { type: "checkpoint"; payload: CheckpointPayload }
  | { type: "document.requested"; payload: DocumentRequestedPayload }
  | { type: "answer"; payload: AnswerPayload }
  | { type: "run.finished"; payload: RunFinishedPayload }
  | { type: "run.error"; payload: RunErrorPayload }
)
const CHAT_TYPES = new Set(["run.started", "step", "question", "checkpoint", "document.requested", "answer", "run.finished", "run.error"])

export interface StreamingSection { sectionId: string; markdown: string }

/** The run-through fields (stages, changes, rerun, stop, mode) are the shared order-sim ones. */
export interface ThreadView extends RunThroughState<Stage> {
  title: string
  /** Highest thread_seq applied. Frames at or below it are duplicates and are dropped. */
  lastSeq: number
  /** thread_seq the getThread snapshot was taken at. Replayed section.saved at or below it leave `draft` alone. */
  snapshotSeq: number
  /** "idle" until a run starts; then the last run-level state the events or snapshot reported. */
  status: string
  pendingQuestion: PendingQuestion | null
  checkpoints: CheckpointView[]
  steps: StepView[]
  /** Run starts (with the user's message), steps, questions, checkpoints, document requests and run endings, in thread_seq order. */
  events: ChatEvent[]
  draft: DraftSnapshot | null
  streamingSection: StreamingSection | null
  staleStages: Stage[]
  /** Count of agent section.saved events applied live; the Draft button badges when it moves while the panel is closed. */
  agentSaves: number
  caseSummary: Record<string, unknown>
  /** Caption facts from the latest intake record; null until intake is recorded. */
  caseFacts: CaseFacts | null
  /** The Nira Matter the draft belongs to (the snapshot's case_id); null when unattached or on an older server. */
  caseId: string | null
  /** True once the getThread snapshot has been applied; until then the page has not read the thread. */
  snapshotLoaded: boolean
  finishedSummary: string | null
  error: { code: string; message: string } | null
}

export type LandedStage = StageLandedPayload & { stale?: boolean }

export function emptyThread(): ThreadView {
  return {
    title: "", lastSeq: 0, snapshotSeq: 0, status: "idle", pendingQuestion: null, checkpoints: [], steps: [], events: [], draft: null,
    streamingSection: null, staleStages: [], agentSaves: 0, caseSummary: {}, caseFacts: null, caseId: null, snapshotLoaded: false, finishedSummary: null, error: null,
    ...emptyRunThrough<Stage>(),
  }
}

/**
 * Pure reducer: one event into a thread view.
 * Frames at or below lastSeq are duplicates. A frame past lastSeq + 1 is a gap: it is not applied,
 * and the transport replays from lastSeq.
 */
export function reduceThread(view: ThreadView, frame: EventFrame): ThreadView {
  if (frame.thread_seq <= view.lastSeq) return view
  if (frame.thread_seq > view.lastSeq + 1) return view
  const next = applyEvent(view, frame)
  const events = CHAT_TYPES.has(frame.type)
    ? [...view.events, { threadSeq: frame.thread_seq, runId: frame.run_id, createdAt: frame.created_at, type: frame.type, payload: frame.payload } as ChatEvent]
    : view.events
  return { ...next, events, lastSeq: frame.thread_seq }
}

/** Step id as the UI keys it; old payloads without an id are unique per event. */
export const stepId = (p: StepPayload, threadSeq: number) => p.id || `seq-${threadSeq}`

export function toStepView(p: StepPayload, threadSeq: number): StepView {
  return {
    id: stepId(p, threadSeq), threadSeq, stage: p.stage ?? null, message: p.label ?? p.message ?? "",
    status: p.status === "running" || p.status === "error" ? p.status : "done", kind: (p.kind ?? "other") as StepKind, detail: p.detail ?? null,
  }
}

function applyEvent(view: ThreadView, frame: EventFrame): ThreadView {
  switch (frame.type) {
    case "run.started":
      return { ...view, status: "running", pendingQuestion: null, finishedSummary: null, error: null, stop: null, mode: frame.payload.mode }
    case "stage.landed":
    case "row.changed":
    case "run.rerun":
    case "stop":
    case "sources":
      return applyRunThrough<Stage, ThreadView>(view, frame as RunThroughFrame<Stage>)
    case "step": {
      const step = toStepView(frame.payload, frame.thread_seq)
      const at = view.steps.findIndex((s) => s.id === step.id)
      if (at < 0) return { ...view, steps: [...view.steps, step] }
      // Same tool call: update in place (running -> done | error), keeping its first position and seq.
      const steps = view.steps.slice()
      steps[at] = { ...step, threadSeq: steps[at].threadSeq }
      return { ...view, steps }
    }
    case "question":
      return { ...view, pendingQuestion: { kind: "question", ...frame.payload } }
    case "checkpoint":
      return {
        ...view,
        pendingQuestion: { kind: "checkpoint", ...frame.payload },
        checkpoints: [...view.checkpoints, { threadSeq: frame.thread_seq, ...frame.payload }],
      }
    case "document.requested":
      return { ...view, pendingQuestion: { kind: "document", ...frame.payload } }
    case "section.delta": {
      const { section_id: sectionId, delta } = frame.payload
      const streaming = view.streamingSection
      const markdown = streaming && streaming.sectionId === sectionId ? streaming.markdown + delta : delta
      return { ...view, streamingSection: { sectionId, markdown } }
    }
    case "section.saved": {
      const { section_id: sectionId, version, author, markdown } = frame.payload
      const streaming = view.streamingSection?.sectionId === sectionId ? view.streamingSection : null
      const streamingSection = streaming ? null : view.streamingSection
      // Saves at or before the snapshot are already reflected in the getDraft snapshot, which is fresher.
      if (frame.thread_seq <= view.snapshotSeq) return { ...view, streamingSection }
      const draft = view.draft ?? { version: 0, sections: {} }
      const body = markdown ?? streaming?.markdown ?? draft.sections[sectionId]?.markdown
      // Never create an empty section from an event: with no text yet it stays "not drafted".
      if (body === undefined) return { ...view, streamingSection }
      return {
        ...view,
        draft: {
          ...draft,
          sections: { ...draft.sections, [sectionId]: { markdown: body, version, author } },
        },
        streamingSection,
        agentSaves: author === "user" ? view.agentSaves : view.agentSaves + 1,
      }
    }
    case "run.awaiting_user":
      return { ...view, status: "awaiting_user" }
    case "run.finished":
      return { ...view, status: "finished", pendingQuestion: null, streamingSection: null, finishedSummary: frame.payload.summary, rerun: null }
    case "run.error":
      return {
        ...view, status: "error", pendingQuestion: null, streamingSection: null, rerun: null,
        error: { code: frame.payload.code, message: frame.payload.message },
      }
    default:
      // Document events are not thread-view state; they go to docs.store.
      return view
  }
}

/** Fold a GET /threads/{id} snapshot into the view. Events replayed afterwards take precedence. */
export function applySnapshot(view: ThreadView, snap: ThreadSnapshot): ThreadView {
  return {
    ...view,
    title: snap.title,
    snapshotSeq: snap.last_seq,
    status: snap.status,
    pendingQuestion: snap.pending_question,
    staleStages: snap.stale_stages,
    caseSummary: snap.case_summary,
    caseFacts: snap.case_facts ?? view.caseFacts,
    caseId: snap.case_id ?? null,
    snapshotLoaded: true,
    ...runThroughSnapshot(view, snap),
  }
}

interface SimStore {
  threads: Record<string, ThreadView>
  apply: (threadId: string, frame: EventFrame) => void
  /** Debounced refetch of GET /draft ground titles (merged into the draft; sections untouched). */
  refreshTitles: (threadId: string) => void
  /** Debounced refetch of the title and caption facts from GET /threads/{id}. */
  refreshFacts: (threadId: string) => void
  /** Load the snapshot, then follow the event log from seq 0 until `signal` aborts. */
  hydrate: (threadId: string, signal?: AbortSignal) => Promise<void>
  /** Replace the whole draft (after a restore or an explicit reload). */
  setDraft: (threadId: string, draft: DraftSnapshot) => void
  /** Record a section the user saved or resolved locally. Never lowers the draft version. */
  setSection: (threadId: string, sectionId: string, section: SectionDraft) => void
  clear: () => void
}

const REFRESH_MS = 400
const refreshTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** A findings section was saved and the draft has no title for it yet. */
function needsTitles(frame: EventFrame, cur: ThreadView): boolean {
  if (frame.type !== "section.saved") return false
  const id = frame.payload.section_id
  return id.startsWith("findings.") && !cur.draft?.titles?.[id]
}

/** The grounds checkpoint was answered (the run that follows it starts with answered_stage). */
const groundsAnswered = (frame: EventFrame) => frame.type === "run.started" && frame.payload.answered_stage === "grounds"

/**
 * Case facts and the title (retitled from intake by the engine) can change when intake lands or a run
 * pauses; refetching the snapshot is cheap.
 */
const mayChangeFacts = (frame: EventFrame) =>
  frame.type === "run.awaiting_user" || frame.type === "run.finished" || (frame.type === "stage.landed" && frame.payload.stage === "intake")

/** Product analytics for a live run outcome. Codes only; never message text. */
function trackRunOutcome(frame: EventFrame, draft: ThreadView["draft"], threadId: string) {
  if (frame.type === "run.finished") trackDraftCompleted("oia", threadId, draft)
  if (frame.type === "run.finished" || frame.type === "run.awaiting_user") track("sim_turn_completed")
  else if (frame.type === "run.error") track("sim_turn_failed", { code: frame.payload.code })
}

const view = (threads: Record<string, ThreadView>, threadId: string) => threads[threadId] ?? emptyThread()

export const useSimStore = create<SimStore>((set, get) => ({
  threads: {},
  apply: (threadId, frame) => {
    const before = view(get().threads, threadId)
    if (frame.type === "run.started") noteRunStart(threadId, frame.created_at)
    set((s) => {
      const cur = view(s.threads, threadId)
      const next = reduceThread(cur, frame)
      return next === cur ? s : { threads: { ...s.threads, [threadId]: next } }
    })
    if (get().threads[threadId] === undefined || frame.thread_seq <= before.lastSeq) return
    // Frames at or below the snapshot are history being replayed, not events that just happened.
    if (frame.thread_seq > before.snapshotSeq) trackRunOutcome(frame, get().threads[threadId]?.draft ?? null, threadId)
    if (needsTitles(frame, before) || groundsAnswered(frame)) get().refreshTitles(threadId)
    if (mayChangeFacts(frame)) get().refreshFacts(threadId)
  },
  refreshTitles: (threadId) => {
    const key = `titles:${threadId}`
    clearTimeout(refreshTimers.get(key))
    refreshTimers.set(key, setTimeout(() => {
      refreshTimers.delete(key)
      getDraft(threadId).then((fresh) => {
        if (!fresh.titles) return
        set((s) => {
          const cur = view(s.threads, threadId)
          if (!cur.draft) return s
          return { threads: { ...s.threads, [threadId]: { ...cur, draft: { ...cur.draft, titles: fresh.titles } } } }
        })
      }).catch(() => { /* titles are cosmetic; the next trigger retries */ })
    }, REFRESH_MS))
  },
  refreshFacts: (threadId) => {
    const key = `facts:${threadId}`
    clearTimeout(refreshTimers.get(key))
    refreshTimers.set(key, setTimeout(() => {
      refreshTimers.delete(key)
      getThread(threadId).then((snap) => {
        set((s) => {
          const cur = view(s.threads, threadId)
          const title = snap.title || cur.title
          const caseFacts = snap.case_facts ?? cur.caseFacts
          if (title === cur.title && caseFacts === cur.caseFacts) return s
          return { threads: { ...s.threads, [threadId]: { ...cur, title, caseFacts } } }
        })
      }).catch(() => { /* the title stays; the caption falls back to placeholders */ })
    }, REFRESH_MS))
  },
  hydrate: async (threadId, signal) => {
    const [snap, draft] = await Promise.all([getThread(threadId, signal), getDraft(threadId, signal)])
    set((s) => {
      const cur = applySnapshot(view(s.threads, threadId), snap)
      return { threads: { ...s.threads, [threadId]: { ...cur, draft } } }
    })
    await followThread(threadId, 0, (frame) => get().apply(threadId, frame), signal)
  },
  setDraft: (threadId, draft) =>
    set((s) => ({ threads: { ...s.threads, [threadId]: { ...view(s.threads, threadId), draft } } })),
  setSection: (threadId, sectionId, section) =>
    set((s) => {
      const cur = view(s.threads, threadId)
      const draft = cur.draft ?? { version: 0, sections: {} }
      const next = { ...draft, version: Math.max(draft.version, section.version), sections: { ...draft.sections, [sectionId]: section } }
      return { threads: { ...s.threads, [threadId]: { ...cur, draft: next } } }
    }),
  clear: () => {
    refreshTimers.forEach((t) => clearTimeout(t))
    refreshTimers.clear()
    set({ threads: {} })
  },
}))
